import { Space } from 'antd';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Customer } from 'store/customers/customer';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import { StyledTabs } from 'components/new-ui/base/tabs/styles';
import {
    CustomerDetailsTabs,
    getActiveTab,
    getConfigurationCompletion,
    isAep5Required,
    isAep5Verified,
    isOverviewComplete,
} from './utils';
import type { ConfigurationStatus as ConfigurationStatusType } from './utils';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { PartyProvider } from './PartyProvider';
import { useRequestPromise } from 'hooks/useRequest';
import { getCustomer } from 'store/customers/client';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { StyledBadge } from 'components/new-ui/base/status-indicator/styles';
import { hasPermissions } from 'config/roles/hasPermissions';
import useSession from 'hooks/useSession';
import useCustomers from 'hooks/useCustomers';
import Tag from 'components/new-ui/base/tag/Tag';
import ConfigurationStatus from './ConfigurationStatus';
import StatusTag from 'components/ui/base/status/StatusTag';

const TabLabel = ({ label, isConfigured }: { label: string; isConfigured: boolean }) => (
    <div style={{ position: 'relative' }}>
        {label}
        {!isConfigured && (
            <span style={{ position: 'absolute', top: 0, right: -15 }}>
                <StatusTag color="var(--warning-normal)" status="" size="small" />
            </span>
        )}
    </div>
);

type Props = {
    children: ReactNode;
};

const PartyPage = ({ children }: Props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { customerId } = useParams();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { userInfo } = useSession();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const {
        data: fetchedCustomer,
        isLoading,
        error,
        refetch: refreshCustomer,
    } = useRequestPromise(
        () => {
            if (!customerId) return null;
            return getCustomer(customerId);
        },
        undefined,
        [customerId]
    );

    const [customer, setCustomer] = useState<Customer | null>(fetchedCustomer ?? null);

    const { jobsCount, invoicesCount, getJobsCount, getInvoicesCount } = useCustomers({ customerId });

    const activeTabKey = getActiveTab(location, customerId);
    const canUpdateCustomers = hasPermissions(userInfo, 'customers', 'UPDATE');

    const configurationStatus: ConfigurationStatusType = useMemo(
        () => ({
            overview: { complete: !!customer && isOverviewComplete(customer) },
            aep5: { required: isAep5Required(customer), complete: isAep5Verified(customer) },
        }),
        [customer]
    );

    const {
        isOverviewConfigured,
        isFilesConfigured,
        isComplete: configurationComplete,
    } = getConfigurationCompletion(configurationStatus);

    const tabs = useMemo(
        () => [
            {
                key: CustomerDetailsTabs.OVERVIEW,
                label: <TabLabel label="Overview" isConfigured={isOverviewConfigured} />,
            },
            {
                key: CustomerDetailsTabs.FILES,
                label: <TabLabel label="Files" isConfigured={isFilesConfigured} />,
            },
            { key: CustomerDetailsTabs.ADDITIONAL_CHARGES, label: 'Additional Charges' },
            {
                key: CustomerDetailsTabs.JOBS,
                label: <>Jobs {jobsCount > 0 && <StyledBadge color="var(--gray-6)" count={jobsCount} />}</>,
            },
            {
                key: CustomerDetailsTabs.INVOICES,
                label: <>Invoices {invoicesCount > 0 && <StyledBadge color="var(--gray-6)" count={invoicesCount} />}</>,
            },
        ],
        [isFilesConfigured, isOverviewConfigured, invoicesCount, jobsCount]
    );

    useEffect(() => {
        setBreadcrumbRoutes([
            { breadcrumbName: 'Home', path: '/home' },
            { breadcrumbName: 'Parties', path: '../customers' },
            { breadcrumbName: 'Party Details', path: '' },
        ]);
    }, [setBreadcrumbRoutes]);

    useEffect(() => {
        if (!isLoading) {
            hideGlobalOverlay();
            return;
        }

        if (isLoading) {
            showGlobalOverlay({
                type: 'LoadingOverlay',
                payload: { message: 'Loading party data...' },
            });
        }

        return () => {
            hideGlobalOverlay();
        };
    }, [hideGlobalOverlay, isLoading, showGlobalOverlay]);

    // Get number of invoices and number of jobs
    useEffect(() => {
        if (!customerId) return;
        getJobsCount(customerId);
        getInvoicesCount(customerId);
    }, [customerId, getJobsCount, getInvoicesCount]);

    useEffect(() => {
        if (fetchedCustomer) {
            setCustomer(fetchedCustomer);
        }
    }, [fetchedCustomer]);

    if (!customerId) {
        return <Navigate to="/customers" />;
    }

    if (isLoading) {
        return null;
    }

    if (error) {
        return <div>Failed to load party</div>;
    }

    if (!customer) {
        return <div>Missing party data</div>;
    }

    return (
        <PartyProvider
            viewOnly={!customer.active}
            customerId={customerId}
            customer={customer}
            canUpdate={canUpdateCustomers}
            setCustomer={setCustomer}
            refreshCustomer={refreshCustomer}
            configurationStatus={configurationStatus}
        >
            <StyledPageWrapper>
                <Space style={{ width: '100%' }} direction="vertical" size={24}>
                    <PageHeader
                        title="Party Details"
                        icon={null}
                        showBackButton
                        onBackButtonClick={() => navigate(-1)}
                        additionalContent={
                            <div style={{ marginLeft: 16, alignSelf: 'center' }}>
                                <Space size={16}>
                                    <ConfigurationStatus status={configurationComplete ? 'complete' : 'incomplete'} />
                                    {!customer.active && <Tag status="default" label="Archived" />}
                                </Space>
                            </div>
                        }
                    />
                    <StyledTabs
                        items={tabs}
                        activeKey={activeTabKey}
                        onChange={(key) => {
                            const activeKey = key as keyof typeof CustomerDetailsTabs;
                            if (activeKey === CustomerDetailsTabs.OVERVIEW) {
                                navigate(`/customers/${customerId}`, { replace: true });
                            } else {
                                navigate(`/customers/${customerId}/${activeKey}`, { replace: true });
                            }
                        }}
                    />
                </Space>
                {children}
            </StyledPageWrapper>
        </PartyProvider>
    );
};

export default PartyPage;
